import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Shield, LogOut, Save, Send, Loader2, Users, Video, Settings2, Ticket, FileText, Megaphone, Link2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { broadcastPush, getSubscriberCount, getSubscriberStats, listRecentBroadcasts, sendTestPush, unsubscribePush, type BroadcastRow } from "@/lib/push.functions";
import { subscribeToPushDetailed, serializeSubscription } from "@/lib/push-client";
import { UTILITY_ALIASES, GOLD_CITY_ALIASES } from "@/lib/alias-routes";
import { getSiteContext, saveRouteVideo, saveSiteSetting } from "@/lib/site-context.functions";
import { IndexingQueuePanel } from "@/components/admin/IndexingQueuePanel";
import { PrizeBondDrawsPanel } from "@/components/admin/PrizeBondDrawsPanel";
import { CrawlerLinksPanel } from "@/components/admin/CrawlerLinksPanel";
import { listSiteFilesAdmin, saveSiteFile, type SiteFileRow } from "@/lib/site-files-admin.functions";

export const Route = createFileRoute("/admin-portal")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Admin Portal — DrHint" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminPortal,
});

function AdminPortal() {
  const [state, setState] = useState<"loading" | "anon" | "not-admin" | "admin">("loading");

  async function check(userIdHint?: string) {
    try {
      let userId = userIdHint;
      if (!userId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setState("anon"); return; }
        userId = user.id;
      }
      const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").maybeSingle();
      setState(roles ? "admin" : "not-admin");
    } catch (err) {
      console.error("[admin-auth]", err);
      setState((s) => (s === "loading" ? "anon" : s));
    }
  }

  useEffect(() => {
    check();
    // V5-PATCH: 2.5s watchdog — never leave the user on a permanent spinner.
    const watchdog = setTimeout(() => {
      setState((s) => (s === "loading" ? "anon" : s));
    }, 2500);
    // CRITICAL: never await supabase APIs inside onAuthStateChange — causes deadlock.
    // Defer with setTimeout(0) and use the session passed to the callback.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") { setState("anon"); return; }
      if (event !== "SIGNED_IN" && event !== "INITIAL_SESSION" && event !== "USER_UPDATED") return;
      const uid = session?.user?.id;
      setTimeout(() => {
        if (!uid) { setState("anon"); return; }
        setState((s) => (s === "loading" ? "loading" : s));
        check(uid);
      }, 0);
    });
    return () => { clearTimeout(watchdog); sub.subscription.unsubscribe(); };
  }, []);

  return (
    <Layout>
      {state === "loading" && (
        <div className="mx-auto max-w-md px-4 py-24 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      )}
      {state === "anon" && <LoginForm />}
      {state === "not-admin" && <NotAdminScreen />}
      {state === "admin" && <Dashboard onLogout={async () => { await supabase.auth.signOut(); }} />}
    </Layout>
  );
}

function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr("");
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (error) setErr(error.message);
  }

  return (
    <section className="mx-auto max-w-md px-4 py-16">
      <div className="glass rounded-3xl p-8">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="h-5 w-5 text-gold" />
          <h1 className="text-2xl font-black">Admin Portal</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">Authorized personnel only. Server-side role check enforced.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground">Email</label>
            <Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" required />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Password</label>
            <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" required />
          </div>
          {err && <p className="text-sm text-red-400">{err}</p>}
          <Button type="submit" disabled={busy} className="w-full bg-gold text-primary-foreground hover:bg-gold/90">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Sign in
          </Button>
        </form>
      </div>
    </section>
  );
}

function NotAdminScreen() {
  return (
    <section className="mx-auto max-w-md px-4 py-16">
      <div className="glass rounded-3xl p-8 text-center">
        <Shield className="h-8 w-8 text-red-400 mx-auto mb-3" />
        <h1 className="text-xl font-black mb-2">Access denied</h1>
        <p className="text-sm text-muted-foreground mb-4">
          You are signed in but not an admin. Ask an existing admin to grant your account the
          <code className="mx-1 rounded bg-muted px-1">admin</code> role in the <code>user_roles</code> table.
        </p>
        <Button variant="outline" onClick={() => supabase.auth.signOut()}>Sign out</Button>
      </div>
    </section>
  );
}

function Dashboard({ onLogout }: { onLogout: () => void }) {
  return (
    <section className="mx-auto max-w-5xl px-4 py-12">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-gold" />
          <h1 className="text-2xl font-black">Admin Dashboard</h1>
        </div>
        <Button variant="ghost" size="sm" onClick={onLogout}>
          <LogOut className="h-4 w-4 mr-1.5" /> Logout
        </Button>
      </div>
      <Link
        to="/admin/earning-apps"
        className="mb-4 flex items-center justify-between rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 hover:bg-emerald-500/20"
      >
        <span className="flex items-center gap-2 font-semibold">
          <span className="text-lg">💸</span>
          Earning Apps Review — Add / edit apps via JSON
        </span>
        <span className="text-xs text-muted-foreground">Open →</span>
      </Link>
      <Link
        to="/admin/blog"
        className="mb-4 flex items-center justify-between rounded-lg border border-sky-500/40 bg-sky-500/10 px-4 py-3 hover:bg-sky-500/20"
      >
        <span className="flex items-center gap-2 font-semibold">
          <span className="text-lg">✍️</span>
          Blog — Add / edit blog posts via JSON
        </span>
        <span className="text-xs text-muted-foreground">Open →</span>
      </Link>
      <Tabs defaultValue="videos">
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="videos"><Video className="h-3.5 w-3.5 mr-1.5" /> Video Embeds</TabsTrigger>
          <TabsTrigger value="seo"><Settings2 className="h-3.5 w-3.5 mr-1.5" /> SEO Settings</TabsTrigger>
          <TabsTrigger value="indexing">Indexing Queue</TabsTrigger>
          <TabsTrigger value="crawler-links"><Link2 className="h-3.5 w-3.5 mr-1.5" /> Crawler Links</TabsTrigger>
          <TabsTrigger value="txt"><FileText className="h-3.5 w-3.5 mr-1.5" /> TXT Files</TabsTrigger>
          <TabsTrigger value="push">Broadcast</TabsTrigger>
          <TabsTrigger value="prize-bond"><Ticket className="h-3.5 w-3.5 mr-1.5" /> Prize Bond Draws</TabsTrigger>
        </TabsList>
        <TabsContent value="videos"><VideoEmbedPanel /></TabsContent>
        <TabsContent value="seo"><SeoSettingsPanel /></TabsContent>
        <TabsContent value="indexing"><IndexingQueuePanel /></TabsContent>
        <TabsContent value="crawler-links"><CrawlerLinksPanel /></TabsContent>
        <TabsContent value="txt"><TxtFilesPanel /></TabsContent>
        <TabsContent value="push"><BroadcastPanel /></TabsContent>
        <TabsContent value="prize-bond"><PrizeBondDrawsPanel /></TabsContent>
      </Tabs>
    </section>
  );
}

function TxtFilesPanel() {
  const list = useServerFn(listSiteFilesAdmin);
  const save = useServerFn(saveSiteFile);
  const [rows, setRows] = useState<SiteFileRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState("");

  useEffect(() => { list().then(setRows).catch(() => setRows([])); }, [list]);

  function update(name: string, patch: Partial<SiteFileRow>) {
    setRows((rs) => rs.map((r) => (r.name === name ? { ...r, ...patch } : r)));
  }

  async function onSave(row: SiteFileRow) {
    setBusy(row.name);
    try {
      await save({ data: { name: row.name, content: row.content, content_type: row.content_type || "text/plain; charset=utf-8" } });
      setMsg((m) => ({ ...m, [row.name]: "Saved ✓ (live in ~10 min due to CDN cache)" }));
      setTimeout(() => setMsg((m) => ({ ...m, [row.name]: "" })), 4000);
    } catch (e) {
      setMsg((m) => ({ ...m, [row.name]: `Error: ${(e as Error).message}` }));
    } finally { setBusy(null); }
  }

  async function addNew() {
    const name = newName.trim();
    if (!name || !/^[a-zA-Z0-9._\-]+$/.test(name)) { alert("Invalid filename (letters, numbers, . _ - only)"); return; }
    setBusy(name);
    try {
      await save({ data: { name, content: "", content_type: name.endsWith(".xml") ? "application/xml; charset=utf-8" : "text/plain; charset=utf-8" } });
      setNewName("");
      const fresh = await list();
      setRows(fresh);
    } catch (e) { alert((e as Error).message); }
    finally { setBusy(null); }
  }

  return (
    <div className="space-y-6">
      <div className="glass rounded-3xl p-6 md:p-8 space-y-3">
        <h2 className="text-lg font-bold flex items-center gap-2"><FileText className="h-5 w-5 text-gold" /> TXT / static file editor</h2>
        <p className="text-sm text-muted-foreground">
          Ye files server-side render hoti hain — save karte hi ~10 minute me live URL par update ho jaayengi (CDN cache).
          Kisi bhi file ka content overwrite kar sakte hain (jaise <code>ads.txt</code>, <code>robots.txt</code>, <code>llms.txt</code>, <code>disavow.txt</code>). Empty content chhodenge to server default fallback bhejega.
        </p>
        <div className="flex items-center gap-2 pt-2">
          <Input placeholder="new-file.txt" value={newName} onChange={(e) => setNewName(e.target.value)} className="max-w-xs" />
          <Button onClick={addNew} disabled={busy === newName || !newName}>Add file</Button>
          <span className="text-xs text-muted-foreground">Note: naya file server route bhi chahiye — ready-to-use routes: ads.txt, robots.txt, llms.txt, disavow.txt</span>
        </div>
      </div>

      {rows.map((r) => (
        <div key={r.name} className="glass rounded-3xl p-6 md:p-8 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-base font-bold flex items-center gap-2">
                <FileText className="h-4 w-4 text-gold" />
                <a href={`/${r.name}`} target="_blank" rel="noopener" className="text-gold hover:underline">/{r.name}</a>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {r.content.length} chars • {r.content_type} • Last saved: {new Date(r.updated_at).toLocaleString()}
              </div>
            </div>
          </div>
          <textarea
            value={r.content}
            onChange={(e) => update(r.name, { content: e.target.value })}
            rows={12} spellCheck={false}
            className="w-full rounded-lg border border-border bg-[oklch(0.1_0.02_265)] p-3 text-xs font-mono text-foreground/85"
          />
          <div className="flex items-center gap-2">
            <Input value={r.content_type} onChange={(e) => update(r.name, { content_type: e.target.value })} className="max-w-xs" placeholder="text/plain; charset=utf-8" />
            <Button disabled={busy === r.name} onClick={() => onSave(r)} className="bg-gold text-primary-foreground hover:bg-gold/90">
              {busy === r.name ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save {r.name}
            </Button>
            {msg[r.name] && <span className={`text-sm ${msg[r.name].startsWith("Error") ? "text-red-400" : "text-emerald-400"}`}>{msg[r.name]}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function BroadcastPanel() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/");
  const [country, setCountry] = useState<string>("");
  const [metal, setMetal] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string>("");
  const [count, setCount] = useState<{ count: number; price_alert: number; legacy: number } | null>(null);
  const [stats, setStats] = useState<{ total: number; byCountry: Record<string, number>; byMetal: Record<string, number>; last24h: number } | null>(null);
  const broadcast = useServerFn(broadcastPush);
  const fetchCount = useServerFn(getSubscriberCount);
  const fetchStats = useServerFn(getSubscriberStats);
  const testPush = useServerFn(sendTestPush);
  const unsub = useServerFn(unsubscribePush);
  const fetchBroadcasts = useServerFn(listRecentBroadcasts);
  const [broadcasts, setBroadcasts] = useState<BroadcastRow[]>([]);
  const [testMsg, setTestMsg] = useState<string>("");

  useEffect(() => {
    fetchCount().then(setCount).catch(() => setCount(null));
    fetchStats().then(setStats).catch(() => setStats(null));
    fetchBroadcasts().then((r) => setBroadcasts(r.rows)).catch(() => {});
  }, [fetchCount, fetchStats, fetchBroadcasts]);

  const filteredCount = (() => {
    if (!stats) return null;
    if (!country && !metal) return stats.total + (count?.legacy ?? 0);
    let n = stats.total;
    if (country) n = stats.byCountry[country] ?? 0;
    // metal filter narrows further (approx)
    if (metal) n = Math.min(n, stats.byMetal[metal] ?? 0);
    return n;
  })();

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (!confirm(`Send "${title}" to ${filteredCount ?? "?"} subscribers now?`)) return;
    setBusy(true); setResult("");
    try {
      const r = await broadcast({ data: { title, body, url, country: country || null, metal: (metal || null) as "gold" | "silver" | null } });
      const sampleError = r.errors?.[0]
        ? `  •  First error: ${r.errors[0].statusCode ?? "?"} ${r.errors[0].error ?? "unknown"}`
        : "";
      setResult(`✓ Sent ${r.sent}/${r.total}  •  Failed ${r.failed}${r.stale?.length ? `  •  Cleaned ${r.stale.length} stale` : ""}${sampleError}`);
      fetchCount().then(setCount).catch(() => {});
      fetchBroadcasts().then((rr) => setBroadcasts(rr.rows)).catch(() => {});
    } catch (err) {
      setResult(`Error: ${(err as Error).message}`);
    } finally { setBusy(false); }
  }

  async function runTest() {
    setTestMsg("Testing…");
    try {
      const r = await subscribeToPushDetailed();
      if (!r.ok) {
        if (r.endpoint) await unsub({ data: { endpoint: r.endpoint } });
        setTestMsg(`❌ ${r.reason}`);
        return;
      }
      const s = serializeSubscription(r.sub);
      const t = await testPush({ data: s });
      if (t.ok) setTestMsg(`✅ Test sent (status ${t.statusCode}) — apni notification tray dekhein.`);
      else setTestMsg(`❌ Test failed: status ${t.statusCode ?? "?"} — ${t.error ?? "unknown"}`);
    } catch (e) { setTestMsg(`❌ ${(e as Error).message}`); }
  }

  const countryEntries = stats ? Object.entries(stats.byCountry).sort((a, b) => b[1] - a[1]) : [];

  return (
    <div className="space-y-6">
      {/* Storage info card */}
      <div className="glass rounded-3xl p-6 md:p-8 space-y-3">
        <h2 className="text-lg font-bold flex items-center gap-2"><Users className="h-4 w-4 text-gold" /> Subscribers — kahan save ho rahe hain</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Har user jo "Price Alert" bell se Subscribe karta hai uska browser endpoint + encryption keys hamare backend database ke <b className="text-foreground">push_subscribers</b> table me save hote hain (columns: <code className="text-xs">endpoint, p256dh, auth, country, metal, threshold_pkr, active, created_at</code>). Purane general subscribers <b className="text-foreground">push_subscriptions</b> table me hain — dono ko yeh broadcast tool ek saath target karta hai (duplicates auto-remove).
        </p>
        <p className="text-xs text-muted-foreground">
          Aap in tables ko kabhi bhi <b>Cloud → Tables</b> se export kar sakte hain (CSV) — future me kisi bhi website ki notification promotion inhi subscribers ko bheji ja sakti hai, sirf yahan title/body/URL badalna hoga.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
          <StatBox label="Total active" value={count?.count ?? "—"} />
          <StatBox label="Price alert opt-ins" value={count?.price_alert ?? "—"} />
          <StatBox label="Legacy generic" value={count?.legacy ?? "—"} />
          <StatBox label="Naye (24h)" value={stats?.last24h ?? "—"} />
        </div>
        {countryEntries.length > 0 && (
          <div className="pt-2">
            <div className="text-xs font-semibold text-muted-foreground mb-1.5">By country</div>
            <div className="flex flex-wrap gap-1.5">
              {countryEntries.map(([c, n]) => (
                <span key={c} className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-card/60 px-2.5 py-1 text-xs">
                  <span className="uppercase">{c}</span>
                  <span className="font-semibold text-gold">{n}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Broadcast composer */}
      <div className="glass rounded-3xl p-6 md:p-8 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2"><Send className="h-4 w-4 text-gold" /> Broadcast Push Notification</h2>
            <p className="text-xs text-muted-foreground mt-1">Chrome / Edge / Firefox / Android par instant notification. Filter select karke sirf specific audience ko bhi bhej sakte hain.</p>
          </div>
          <div className="text-sm font-semibold text-gold flex items-center gap-1.5">
            <Users className="h-4 w-4" /> Target: {filteredCount ?? "—"}
          </div>
        </div>

        <form onSubmit={send} className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <label className="text-xs font-semibold text-muted-foreground">
              Country filter
              <select value={country} onChange={(e) => setCountry(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground">
                <option value="">All countries</option>
                {countryEntries.map(([c, n]) => <option key={c} value={c}>{c.toUpperCase()} ({n})</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-muted-foreground">
              Metal filter
              <select value={metal} onChange={(e) => setMetal(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground">
                <option value="">Gold + Silver (all)</option>
                <option value="gold">Gold subscribers only</option>
                <option value="silver">Silver subscribers only</option>
              </select>
            </label>
          </div>

          <div>
            <Input placeholder='Title — e.g. "Gold price alert — Rs 3,000 upar"' value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} required />
            <div className="text-[10px] text-muted-foreground mt-1 text-right">{title.length}/120</div>
          </div>
          <div>
            <textarea
              placeholder='Body — e.g. "24K tola Rs 4,26,000. Live rate check karein."'
              value={body} onChange={(e) => setBody(e.target.value)}
              maxLength={300} required rows={3}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
            <div className="text-[10px] text-muted-foreground mt-1 text-right">{body.length}/300</div>
          </div>
          <div>
            <Input placeholder="Target URL (e.g. /pakistan  or  https://your-other-site.com/page)" value={url} onChange={(e) => setUrl(e.target.value)} maxLength={500} required />
            <p className="text-[10px] text-muted-foreground mt-1">
              Kisi bhi URL par bhej sakte hain — apni doosri website promote karni ho toh yahan full <code>https://...</code> paste karein.
            </p>
          </div>

          {(title || body) && (
            <div className="rounded-xl border border-border/70 bg-card/40 p-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Live preview</div>
              <div className="flex items-start gap-2">
                <div className="h-8 w-8 rounded bg-gold/20 flex items-center justify-center text-gold font-bold text-xs">Dr</div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{title || "Title…"}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{body || "Message body will appear here…"}</div>
                  <div className="text-[10px] text-muted-foreground/70 mt-0.5">drhint.com</div>
                </div>
              </div>
            </div>
          )}

          <Button type="submit" disabled={busy || !title || !body} className="bg-gold text-primary-foreground hover:bg-gold/90">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Broadcast to {filteredCount ?? 0} subscribers
          </Button>
          {result && <p className="text-sm text-emerald-400">{result}</p>}
        </form>

        <div className="rounded-xl border border-border/50 bg-card/40 p-4 space-y-2">
          <div className="text-sm font-semibold">🧪 End-to-end test</div>
          <p className="text-xs text-muted-foreground">Broadcast bhejne se pehle isi admin-panel wale browser/device par test notification bhejein — confirm ho jayega ki VAPID key match hai aur delivery pipeline kaam kar rahi hai.</p>
          <Button type="button" variant="outline" onClick={runTest}>Send test to my browser</Button>
          {testMsg && <p className="text-xs">{testMsg}</p>}
        </div>

        <div className="text-[11px] text-muted-foreground border-t border-border/50 pt-3 leading-relaxed">
          <b className="text-foreground">Kya hoga:</b> Title + body user ke phone/desktop par native OS notification ke roop me show hoga. Click karte hi diya gaya URL ussi tab me khulega.<br />
          <b className="text-foreground">Kya nahi hoga:</b> Koi email, SMS ya phone number nahi bhejta. Personal data (name/email) hamare paas hai bhi nahi — sirf browser ka anonymous push endpoint save hota hai. User kabhi bhi bell se "Turn off" kar sakta hai.
        </div>
      </div>

      {/* Broadcast history + click analytics */}
      <div className="glass rounded-3xl p-6 md:p-8 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2"><Megaphone className="h-4 w-4 text-gold" /> Recent broadcasts (last 30)</h2>
          <button
            type="button"
            onClick={() => fetchBroadcasts().then((r) => setBroadcasts(r.rows)).catch(() => {})}
            className="text-xs font-semibold text-gold hover:underline"
          >Refresh</button>
        </div>
        <p className="text-xs text-muted-foreground">
          Note: ab hum inactive subscribers ko delete/deactivate nahi karte — har broadcast me unhe bhi try karta hai (kya pata baad me device online ho jaye). Click column notification par kis kis ne click kiya wo count hai.
        </p>
        {broadcasts.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">Abhi tak koi broadcast nahi bheja gaya.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground border-b border-border/60">
                <tr>
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Title</th>
                  <th className="py-2 pr-3">Target</th>
                  <th className="py-2 pr-3">Sent</th>
                  <th className="py-2 pr-3">Failed</th>
                  <th className="py-2 pr-3">Clicks</th>
                  <th className="py-2 pr-3">CTR</th>
                  <th className="py-2 pr-3">Filter</th>
                </tr>
              </thead>
              <tbody>
                {broadcasts.map((b) => {
                  const ctr = b.sent > 0 ? ((b.clicks / b.sent) * 100).toFixed(1) + "%" : "—";
                  const filter = [b.country, b.metal].filter(Boolean).join(" / ") || "all";
                  return (
                    <tr key={b.id} className="border-b border-border/30">
                      <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">
                        {new Date(b.created_at).toLocaleString()}
                      </td>
                      <td className="py-2 pr-3 font-medium max-w-[220px] truncate" title={b.title}>{b.title}</td>
                      <td className="py-2 pr-3">{b.target_count}</td>
                      <td className="py-2 pr-3 text-emerald-400 font-semibold">{b.sent}</td>
                      <td className="py-2 pr-3 text-red-400">{b.failed}</td>
                      <td className="py-2 pr-3 text-gold font-bold">{b.clicks}</td>
                      <td className="py-2 pr-3">{ctr}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{filter}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-xl border border-border/70 bg-card/60 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-bold text-gold">{value}</div>
    </div>
  );
}

const VIDEO_ROUTES = [
  ...UTILITY_ALIASES.map((u) => ({ slug: u.slug, label: `/${u.slug}` })),
  ...GOLD_CITY_ALIASES.map((g) => ({ slug: g.slug, label: `/${g.slug}` })),
];

function VideoEmbedPanel() {
  const fetchCtx = useServerFn(getSiteContext);
  const save = useServerFn(saveRouteVideo);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ slug: string; text: string; ok: boolean } | null>(null);

  useEffect(() => {
    fetchCtx().then((c) => {
      const v: Record<string, string> = {};
      for (const [slug, row] of Object.entries(c.videos)) v[slug] = row.video_id;
      setValues(v);
    }).catch(() => {});
  }, [fetchCtx]);

  async function onSave(slug: string) {
    setBusy(slug); setMsg(null);
    try {
      await save({ data: { slug, videoId: values[slug] ?? "" } });
      setMsg({ slug, text: (values[slug] ?? "").trim() ? "Saved ✓" : "Removed ✓", ok: true });
    } catch (e) {
      setMsg({ slug, text: (e as Error).message, ok: false });
    } finally { setBusy(null); }
  }

  return (
    <div className="glass rounded-3xl p-6 md:p-8 space-y-4">
      <div>
        <h2 className="text-lg font-bold flex items-center gap-2"><Video className="h-4 w-4 text-gold" /> 📹 Video Snippet Embed Dashboard</h2>
        <p className="text-xs text-muted-foreground mt-1">Paste a YouTube video ID or full URL per route. Saved videos render above-the-fold with a VideoObject JSON-LD schema for Search Console video indexing. Leave blank and Save to remove.</p>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-card/60 text-left text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-2 font-semibold">Route</th>
              <th className="px-4 py-2 font-semibold">YouTube Video ID / URL</th>
              <th className="px-4 py-2 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {VIDEO_ROUTES.map((r) => (
              <tr key={r.slug} className="border-t border-border align-middle">
                <td className="px-4 py-2 font-mono text-xs text-foreground/80 whitespace-nowrap">{r.label}</td>
                <td className="px-4 py-2">
                  <Input value={values[r.slug] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [r.slug]: e.target.value }))} placeholder="e.g. dQw4w9WgXcQ or https://youtu.be/..." />
                  {msg?.slug === r.slug && <p className={`mt-1 text-xs ${msg.ok ? "text-emerald-400" : "text-red-400"}`}>{msg.text}</p>}
                </td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" disabled={busy === r.slug} onClick={() => onSave(r.slug)} className="bg-gold text-primary-foreground hover:bg-gold/90">
                    {busy === r.slug ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save Link"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SeoSettingsPanel() {
  const fetchCtx = useServerFn(getSiteContext);
  const save = useServerFn(saveSiteSetting);
  const [suffix, setSuffix] = useState("");
  const [tools, setTools] = useState(true);
  const [dashboard, setDashboard] = useState(true);
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchCtx().then((c) => {
      setSuffix(c.settings.title_suffix ?? "");
      setTools(c.settings.home_show_tools !== false);
      setDashboard(c.settings.home_show_dashboard !== false);
    }).catch(() => {});
  }, [fetchCtx]);

  async function persist(key: "title_suffix" | "home_show_tools" | "home_show_dashboard", value: string | boolean) {
    setBusy(true);
    try {
      await save({ data: { key, value } });
      setSaved(`Saved ✓`);
      setTimeout(() => setSaved(""), 1500);
    } catch (e) {
      setSaved(`Error: ${(e as Error).message}`);
    } finally { setBusy(false); }
  }

  return (
    <div className="glass rounded-3xl p-6 md:p-8 space-y-6">
      <div>
        <h2 className="text-lg font-bold flex items-center gap-2"><Settings2 className="h-4 w-4 text-gold" /> General Website SEO Settings</h2>
        <p className="text-xs text-muted-foreground mt-1">Server-rendered. Changes appear on the next page render and propagate to crawlers within minutes.</p>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-semibold">Global title suffix</label>
        <p className="text-xs text-muted-foreground">Appended to every programmatic page title (e.g. "— Live Report" or "— Updated Hourly"). Leave blank to keep titles unchanged.</p>
        <div className="flex gap-2">
          <Input value={suffix} onChange={(e) => setSuffix(e.target.value)} placeholder="— Live Report" maxLength={120} />
          <Button disabled={busy} onClick={() => persist("title_suffix", suffix)} className="bg-gold text-primary-foreground hover:bg-gold/90"><Save className="h-4 w-4 mr-1.5" /> Save</Button>
        </div>
      </div>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">Home interface toggles</h3>
        <ToggleRow label="Show utility tools container grid" checked={tools} onChange={(v) => { setTools(v); persist("home_show_tools", v); }} />
        <ToggleRow label="Show financial dashboard grid (snapshot + watchlist)" checked={dashboard} onChange={(v) => { setDashboard(v); persist("home_show_dashboard", v); }} />
      </div>
      {saved && <p className="text-sm text-emerald-400">{saved}</p>}
      <div className="rounded-xl border border-border bg-card/40 p-4 text-xs text-muted-foreground">
        <div className="font-semibold text-foreground mb-1">Server-controlled (no manual override)</div>
        The automated internal anchor matrix and anomaly thresholds are intentionally kept out of this panel and remain fully automated.
      </div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-border bg-card/40 p-3">
      <span className="text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}